/**
 * Registered-market data — the client for /api/market-data.
 *
 * Source: Ministry of Justice sale deeds + REGA rent indicators, packaged by the
 * market engine. Everything here describes an AREA. Nothing here values a
 * property, and no function should ever be added that does.
 *
 * "Not enough data" is a normal answer, not a failure — the API returns 200 with
 * `available: false`, and so does every function below when a request fails, so
 * callers only ever handle one shape.
 *
 * Requests carry the signed-in viewer's token: the market gate answers /district and
 * /lookup by viewer. The property-page card (/property/:id) stays public either way.
 */

import { apiRequest } from '../utils/apiClient';
import { getAuthToken } from '../utils/authToken';
import type { MarketCard, MarketStatus } from '../types/marketData';

interface Envelope<T> {
  data: T;
}

async function safeGet(path: string, signal?: AbortSignal): Promise<MarketCard> {
  try {
    const res = await apiRequest<Envelope<MarketCard>>(path, {
      signal,
      token: getAuthToken() || undefined,
    });
    return res.data;
  } catch {
    // A network or server problem must not break the page it sits on.
    return { available: false, reason: 'request-failed' };
  }
}

/** Market context for a saved listing. */
export function getPropertyMarketCard(propertyId: string, signal?: AbortSignal): Promise<MarketCard> {
  return safeGet(`/market-data/property/${encodeURIComponent(propertyId)}`, signal);
}

export interface MarketLookupInput {
  city?: string;
  district?: string;
  lat?: number;
  lng?: number;
  propertyType?: string;
  areaM2?: number;
  price?: number;
  listingType?: string;
}

/**
 * Market context before a listing exists — the price check inside the property
 * form, where the broker has typed a district, an area and an asking price.
 */
export function lookupMarketCard(input: MarketLookupInput, signal?: AbortSignal): Promise<MarketCard> {
  const params = new URLSearchParams();
  Object.entries(input).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  return safeGet(`/market-data/lookup?${params.toString()}`, signal);
}

/** Everything known about one district, with no listing to compare against. */
export function getDistrictMarketCard(placeId: string, signal?: AbortSignal): Promise<MarketCard> {
  return safeGet(`/market-data/district/${encodeURIComponent(placeId)}`, signal);
}

/** Whether the snapshot is loaded at all, and how fresh it is. */
export async function getMarketStatus(signal?: AbortSignal): Promise<MarketStatus> {
  try {
    const res = await apiRequest<Envelope<MarketStatus>>('/market-data/status', { signal });
    return res.data;
  } catch {
    return { available: false };
  }
}
